// docs/64 village polish: how people get about a village. Positions are stations (a figure's
// top-left, in cells, as `layout.ts` gives them). The walkable floor is a grid of half cells over the
// band of rows people stand on (the lane outside; the floor in front of the furniture inside); a
// cell is closed where a prop or a pew stands under a figure's feet. A walk is an A* path over that
// grid, smoothed, and a band walks it in single file: each follower treads the same line a little
// behind the one in front (`fileAlong`).
import { VERGE, type Prop, type Room, type VillageLayout } from './layout';

export interface Pt { x: number; y: number }

/** Grid steps per cell. */
const RES = 2;

export interface WalkGrid {
  /** Station x range and y range people may stand in (inclusive). */
  x0: number; x1: number; y0: number; y1: number;
  /** Is a station at (x, y) clear of furniture? */
  free(x: number, y: number): boolean;
}

/** Something in the way: a prop's square, and whether it is drawn over the people (`front`). */
interface Solid { x: number; y: number; front?: boolean }

/** A station whose feet (`y + 0.9`) stand on a prop's square is blocked by it. A prop drawn over the
 * people (a counter, a table, a stool) also blocks the row in front of it, where it would hide a body. */
function blocks(p: Solid, x: number, y: number): boolean {
  return Math.abs(x - p.x) < 0.8 && y >= p.y - 0.75 && y <= p.y + (p.front ? 0.9 : 0.25);
}

function gridOf(x0: number, x1: number, y0: number, y1: number, solid: Solid[]): WalkGrid {
  return {
    x0, x1, y0, y1,
    free: (x, y) => x >= x0 - 1e-6 && x <= x1 + 1e-6 && y >= y0 - 1e-6 && y <= y1 + 1e-6 && !solid.some((p) => blocks(p, x, y)),
  };
}

/** The lane: rows 5–6 of stations, the whole width (the band's corner too); the well and the stalls are in the way. */
export function streetGrid(l: VillageLayout): WalkGrid {
  const solid = l.props.filter((p) => p.layer === 'mid' && p.y >= 5 && p.y < VERGE).map((p) => ({ x: p.x + (p.dx ?? 0) / 24, y: p.y }));
  return gridOf(-1.5, l.w + 0.5, 5.1, 6.7, solid);
}

/** A room's floor in front of the furniture against the back wall; tables, stools, chests and pews are in the way. */
export function roomGrid(room: Room): WalkGrid {
  const F = room.floor;
  const solid: Solid[] = room.props
    .filter((p: Prop) => p.y >= F + 1 && p.layer !== 'back')
    .map((p) => ({ x: p.x + (p.dx ?? 0) / 24, y: p.y + (p.dy ?? 0) / 24, front: p.layer === 'front' }));
  for (const pew of room.pews) for (let i = 0; i < pew.w; i++) solid.push({ x: pew.x + i, y: pew.y - 0.4, front: true });
  // docs/116: what the inn's own layer paints (the dice table, the beds upstairs)
  for (const b of room.blocks ?? []) solid.push(b);
  return gridOf(0.6, room.w - 1.6, F + 1, room.h - 1.6, solid);
}

/** A grid point's key. Columns run negative off the street's left edge (passers walk in from x −1.4), so
 * both are offset: `%` of a negative key gives a negative row, and a walker decoded there flies up the sky. */
const OFF = 1024;
const key = (i: number, j: number) => (i + OFF) * 4096 + (j + OFF);
const unkey = (k: number): Pt => ({ x: (Math.floor(k / 4096) - OFF) / RES, y: ((k % 4096) - OFF) / RES });
const snap = (v: number) => Math.round(v * RES) / RES;

/** The nearest free grid point to `p` (searching outwards a few cells), or `p` itself if none is near. */
export function nearestFree(g: WalkGrid, p: Pt): Pt {
  const cx = Math.min(g.x1, Math.max(g.x0, snap(p.x))), cy = Math.min(g.y1, Math.max(g.y0, snap(p.y)));
  let best: Pt | undefined, bd = Infinity;
  for (let dy = -4; dy <= 4; dy++) for (let dx = -8; dx <= 8; dx++) {
    const x = cx + dx / RES, y = cy + dy / RES;
    if (!g.free(x, y)) continue;
    const d = Math.hypot(x - p.x, (y - p.y) * 1.5);
    if (d < bd) { bd = d; best = { x, y }; }
  }
  return best ?? { x: cx, y: cy };
}

/**
 * A* over the half-cell grid from `a` to `b` (both snapped to the nearest free point), 8 ways with
 * no corner cutting, then smoothed: straight runs the floor allows are walked straight. The path
 * starts at `a` itself and ends at `b`'s free point.
 */
export function findPath(g: WalkGrid, a: Pt, b: Pt): Pt[] {
  const s = nearestFree(g, a), t = nearestFree(g, b);
  const toI = (v: number) => Math.round(v * RES);
  const si = toI(s.x), sj = toI(s.y), ti = toI(t.x), tj = toI(t.y);
  const ok = (i: number, j: number) => g.free(i / RES, j / RES);
  const open: Array<{ i: number; j: number; f: number }> = [{ i: si, j: sj, f: 0 }];
  const gs = new Map<number, number>([[key(si, sj), 0]]);
  const from = new Map<number, number>();
  const h = (i: number, j: number) => Math.hypot(i - ti, j - tj);
  let found = si === ti && sj === tj;
  for (let guard = 0; open.length && !found && guard < 20000; guard++) {
    let bi = 0;
    for (let k = 1; k < open.length; k++) if (open[k]!.f < open[bi]!.f) bi = k;
    const cur = open.splice(bi, 1)[0]!;
    const cg = gs.get(key(cur.i, cur.j))!;
    for (let di = -1; di <= 1; di++) for (let dj = -1; dj <= 1; dj++) {
      if (!di && !dj) continue;
      const ni = cur.i + di, nj = cur.j + dj;
      if (!ok(ni, nj) || (di && dj && (!ok(cur.i + di, cur.j) || !ok(cur.i, cur.j + dj)))) continue;
      const ng = cg + (di && dj ? Math.SQRT2 : 1);
      const k = key(ni, nj);
      if (ng >= (gs.get(k) ?? Infinity)) continue;
      gs.set(k, ng); from.set(k, key(cur.i, cur.j));
      if (ni === ti && nj === tj) { found = true; break; }
      open.push({ i: ni, j: nj, f: ng + h(ni, nj) });
    }
  }
  if (!found) return [a, t];
  const cells: Pt[] = [];
  for (let k: number | undefined = key(ti, tj); k !== undefined; k = from.get(k)) cells.push(unkey(k));
  cells.reverse();
  return smooth(g, [a, ...cells.slice(1)]);
}

/** Can a walker go straight from `a` to `b` without stepping on anything? */
function clear(g: WalkGrid, a: Pt, b: Pt): boolean {
  const n = Math.ceil(Math.hypot(b.x - a.x, b.y - a.y) * RES * 2);
  for (let k = 1; k < n; k++) { const t = k / n; if (!g.free(snap(a.x + (b.x - a.x) * t), snap(a.y + (b.y - a.y) * t))) return false; }
  return true;
}

function smooth(g: WalkGrid, pts: Pt[]): Pt[] {
  if (pts.length <= 2) return pts;
  const out: Pt[] = [pts[0]!];
  let i = 0;
  while (i < pts.length - 1) {
    let j = pts.length - 1;
    while (j > i + 1 && !clear(g, pts[i]!, pts[j]!)) j--;
    out.push(pts[j]!);
    i = j;
  }
  return out;
}

/** Where to stand to talk to someone at `npc`: beside them on the side the walker comes from, else the other. */
export function besideSpot(g: WalkGrid, npc: Pt, from: Pt): Pt {
  const side = from.x <= npc.x ? -1 : 1;
  const y = Math.min(g.y1, Math.max(g.y0, npc.y));
  for (const s of [side, -side]) for (const d of [1, 1.5, 2]) {
    const p = { x: snap(npc.x + s * d), y: snap(y) };
    if (g.free(p.x, p.y)) return p;
  }
  return nearestFree(g, { x: npc.x + side, y });
}

/** The length of a polyline. */
export function lengthOf(path: readonly Pt[]): number {
  let n = 0;
  for (let i = 1; i < path.length; i++) n += Math.hypot(path[i]!.x - path[i - 1]!.x, path[i]!.y - path[i - 1]!.y);
  return n;
}

/** The point `s` along a polyline (clamped to its ends) and which way it is heading there (−1 left, 1 right, 0 straight up or down). */
export function pointAt(path: readonly Pt[], s: number): Pt & { dir: number } {
  if (!path.length) return { x: 0, y: 0, dir: 0 };
  let left = Math.max(0, s);
  for (let i = 1; i < path.length; i++) {
    const a = path[i - 1]!, b = path[i]!;
    const d = Math.hypot(b.x - a.x, b.y - a.y);
    const dir = Math.abs(b.x - a.x) < 1e-6 ? 0 : Math.sign(b.x - a.x);
    if (left <= d || i === path.length - 1) { const t = d ? Math.min(1, left / d) : 1; return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t, dir }; }
    left -= d;
  }
  const p = path[path.length - 1]!;
  return { ...p, dir: 0 };
}

/** How far apart (cells) the band walks in single file. */
export const FILE_GAP = 0.85;

/**
 * Single file: the leader's path from where it stands, with the followers' own places put in
 * front of it (the last follower's first), so each treads the line the one ahead walked. Returns
 * the shared line and, for each walker (leader first), where on it they start and where they stop
 * (each `FILE_GAP` further back than the one in front).
 */
export function fileAlong(path: readonly Pt[], followers: readonly Pt[]): { line: Pt[]; from: number[]; to: number[] } {
  const back = [...followers].reverse();
  const line = [...back, ...path];
  const lead = lengthOf(line) - lengthOf(path);
  const from: number[] = [lead];
  let s = lead;
  for (let i = 0; i < followers.length; i++) {
    const a = i === 0 ? path[0]! : followers[i - 1]!, b = followers[i]!;
    s -= Math.hypot(a.x - b.x, a.y - b.y);
    from.push(Math.max(0, s));
  }
  const end = lengthOf(line);
  const to = from.map((f, i) => Math.max(f, end - i * FILE_GAP));
  return { line, from, to };
}

/**
 * docs/120 VM4: the nearest free grid point to `p` that stands clear of everyone in `taken` (no two
 * people on one spot), searching outwards a few cells; `p`'s own nearest free point if none is clear.
 */
export function clearSpot(g: WalkGrid, p: Pt, taken: readonly Pt[], gap: Pt = { x: 0.6, y: 0.45 }): Pt {
  const clear = (q: Pt) => !taken.some((t) => Math.abs(t.x - q.x) < gap.x && Math.abs(t.y - q.y) < gap.y);
  const s = nearestFree(g, p);
  if (clear(s)) return s;
  let best: Pt | undefined, bd = Infinity;
  for (let dy = -4; dy <= 4; dy++) for (let dx = -12; dx <= 12; dx++) {
    const x = snap(s.x) + dx / RES, y = snap(s.y) + dy / RES;
    if (x < g.x0 - 1e-6 || x > g.x1 + 1e-6 || y < g.y0 - 1e-6 || y > g.y1 + 1e-6 || !g.free(x, y)) continue;
    const q = { x, y };
    if (!clear(q)) continue;
    const d = Math.hypot(x - p.x, (y - p.y) * 1.5);
    if (d < bd) { bd = d; best = q; }
  }
  return best ?? s;
}
